/**
 * Drill-downs, the pure half.
 *
 * The open drill is `drill=<report>:<level>/<key>/<key>/<key>` in the address
 * bar, with at most three keys. A key is percent-encoded on its own, so a key
 * that holds `/` stays one key.
 *
 * `useTableQuery` is the one owner of that parameter: it copies `drill`
 * through every table write. `alert-view.ts` drops it, so a saved alert never
 * stores a transient drill. Do not also declare `drill` as a view parameter.
 * Two owners would fight.
 */

import { formatCount, formatMoney } from './format.ts'
import { PERIOD_PRESETS } from './period-presets.ts'

/** The reserved URL parameter that holds the open drill. */
export const DRILL_PARAM = 'drill'

/** The most keys a drill carries. */
export const MAX_DRILL_KEYS = 3

/**
 * How far a money total may differ from the figure the reader clicked.
 *
 * One cent. A sum of rounded amounts can miss the clicked figure by less
 * than that. A count does not use this. A count matches only when it is exact.
 */
export const MONEY_TOLERANCE = 0.01

/** A level id, `<report>:<name>`. Anything else in the URL is not a drill. */
const LEVEL_PATTERN = /^[a-z0-9-]+:[a-z0-9-]+$/

/** Which drill is open, and what was clicked on the way to it. */
export interface DrillRef {
  level: string
  keys: readonly string[]
}

/** What the clicked figure is, which decides how a total is compared. */
export type DrillMeasureUnit = 'count' | 'money'

/**
 * The figure on the row or the point that was clicked.
 *
 * It is not part of the URL. A link opened cold has none, and the sheet
 * then shows the rows without a comparison.
 */
export interface DrillExpectation {
  /** The column that should add up to the clicked figure. */
  measure: string
  value: number
  /** What the figure is called on the page it came from. */
  label: string
  unit: DrillMeasureUnit
}

/** The report scope a drill shows beside its rows. */
export interface DrillScope {
  /** The period in words, including the days it covers. */
  period: string
  /** The zone the report queried in. */
  timeZone: string
  /** Empty means every merchant. */
  merchants: readonly string[]
}

/** How a drill's total compares with the figure the reader clicked. */
export interface DrillReconciliation {
  /** True when the drill adds up to the clicked figure. */
  matches: boolean
  /** The drill's total minus the clicked figure. */
  difference: number
  /** Shown when the totals do not match. Null when they do. */
  message: string | null
}

/**
 * Write a drill into the value the URL carries.
 *
 * @param ref - The level and its keys.
 * @returns `level/key/key`, each key percent-encoded.
 */
export function formatDrillParam(ref: DrillRef): string {
  return [ref.level, ...ref.keys.map(key => encodeURIComponent(key))].join('/')
}

/**
 * Read a drill from the URL's value.
 *
 * A value that is not a level and at most three keys is no drill, so a
 * hand-edited address opens nothing.
 *
 * @param value - The raw parameter, as the router parsed it.
 * @returns The drill, or null.
 */
export function parseDrillParam(value: unknown): DrillRef | null {
  if (typeof value !== 'string' || value === '') return null
  const [level, ...encoded] = value.split('/')
  if (!LEVEL_PATTERN.test(level) || encoded.length > MAX_DRILL_KEYS) return null
  const keys: string[] = []
  for (const part of encoded) {
    try {
      const key = decodeURIComponent(part)
      if (key === '') return null
      keys.push(key)
    } catch {
      return null
    }
  }

  return { level, keys }
}

/**
 * The drill one level above this one.
 *
 * Dropping the last key is the way up. A drill with no keys has nowhere
 * above it, and the caller closes it.
 *
 * @param ref - The drill on screen.
 * @returns The drill above, or null when this is the top.
 */
export function drillAbove(ref: DrillRef): DrillRef | null {
  if (ref.keys.length === 0) return null

  return { level: ref.level, keys: ref.keys.slice(0, -1) }
}

/**
 * The period line a drill shows.
 *
 * @param periodId - The report's period id, when the URL carries one.
 * @param from - The first civil day.
 * @param to - The last civil day.
 * @returns The period and the days, in one line.
 */
export function drillPeriodLabel(
  periodId: string | undefined,
  from: string,
  to: string
): string {
  const name =
    PERIOD_PRESETS.find(preset => preset.id === periodId)?.label ??
    'Custom range'

  return `${name}, ${from} to ${to}`
}

/**
 * The merchant line a drill shows.
 *
 * @param merchants - The merchant names or ids in force. Empty means every merchant.
 * @returns The line.
 */
export function drillMerchantLabel(merchants: readonly string[]): string {
  if (merchants.length === 0) return 'All merchants'

  return merchants.join(', ')
}

/**
 * Whether a drill adds up to the figure the reader clicked.
 *
 * A count matches only when the total is the figure. Money matches within
 * one cent, compared as integer cents, so one cent is one cent.
 *
 * @param total - The drill's total for the clicked measure, or nothing.
 * @param expected - The figure on the row or the point.
 * @param unit - Whether that figure is a count or money.
 * @param label - What to call the figure in a mismatch. Defaults to a plain phrase.
 * @returns The comparison, or null when there is no total to compare.
 */
export function reconcileDrill(
  total: number | null | undefined,
  expected: number,
  unit: DrillMeasureUnit,
  label?: string
): DrillReconciliation | null {
  if (typeof total !== 'number' || !Number.isFinite(total)) return null
  if (!Number.isFinite(expected)) return null
  const name = label?.trim() ? label.trim() : 'The figure you clicked'
  if (unit === 'count') {
    const difference = total - expected
    const matches = difference === 0

    return {
      matches,
      difference,
      message: matches
        ? null
        : `The rows total ${formatCount(total)}. ${name} is ${formatCount(expected)}.`
    }
  }
  const cents = Math.round(total * 100) - Math.round(expected * 100)
  const matches = Math.abs(cents) <= Math.round(MONEY_TOLERANCE * 100)
  const difference = cents / 100

  return {
    matches,
    difference,
    message: matches
      ? null
      : `The rows total ${formatMoney(total)}. ${name} is ${formatMoney(expected)}.`
  }
}
